import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import { getCollectionData,getDocData } from '@src/packages/utils';
import { createOperation } from '@src/util/firebase-operations/utils/createOperation';
import safeExecute from '@src/util/safeExecute';

class ExceptionRepository {
  constructor() {
    this.exceptionsCollection = FSCollections.EXCEPTIONS;
    this.organizationCollection = FSCollections.ORGANIZATIONS;
  }

  /**
   * Get the Firestore collection path for exceptions
   * @param {string} orgId - Organization ID
   * @returns {string} Collection path
   */
  getExceptionsPath(orgId) {
    return `${this.organizationCollection}/${orgId}/${this.exceptionsCollection}`;
  }

  /**
   * Create a new exception
   * @param {Object} exceptionData - Exception data to create
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Created exception
   */
  async create(exceptionData, organizationId) {
    return safeExecute(async () => {
      const collectionName = this.getExceptionsPath(organizationId);
      const res = await createOperation(collectionName, null, exceptionData, 'create');
      return res;
    });
  }

  /**
   * Update an existing exception
   * @param {string} exceptionId - Exception ID to update
   * @param {Object} exceptionData - Updated exception data
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Updated exception
   */
  async update(exceptionId, exceptionData, organizationId) {
    return safeExecute(async () => {
      const collectionName = this.getExceptionsPath(organizationId);
      const res = await createOperation(collectionName, exceptionId, exceptionData, 'update');
      return res;
    });
  }

  /**
   * Delete an exception
   * @param {string} exceptionId - Exception ID to delete
   * @param {string} organizationId - Organization ID
   * @returns {Promise<void>}
   */
  async delete(exceptionId, organizationId) {
    return safeExecute(async () => {
      const collectionName = this.getExceptionsPath(organizationId);
      await createOperation(collectionName, exceptionId, null, 'delete');
    });
  }

  /**
   * Get an exception by ID
   * @param {string} exceptionId - Exception ID
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Exception data
   */
  async getExceptionById(exceptionId, organizationId) {
    return safeExecute(async () => {
      const exceptionRef = doc(db, `${this.getExceptionsPath(organizationId)}/${exceptionId}`);
      const docSnap = await getDoc(exceptionRef);
      const exceptionData = getDocData(docSnap);

      return exceptionData;
    });
  }

  /**
   * Get all exceptions for an organization
   * @param {string} organizationId - Organization ID
   * @param {Object} filters - Optional filters
   * @returns {Promise<Array>} Array of exceptions
   */
  async getAllExceptions(organizationId, filters = {}) {
    return safeExecute(async () => {
      const exceptionsRef = collection(db, this.getExceptionsPath(organizationId));
      
      let exceptionsQuery = query(exceptionsRef);

      // Apply filters if provided
      if (filters.userId) {
        exceptionsQuery = query(exceptionsQuery, where('user.id', '==', filters.userId));
      }

      if (filters.type) {
        exceptionsQuery = query(exceptionsQuery, where('type', '==', filters.type));
      }

      if (filters.isExcused !== undefined) {
        exceptionsQuery = query(exceptionsQuery, where('isExcused', '==', filters.isExcused));
      }

      const querySnapshot = await getDocs(exceptionsQuery);
      const exceptions = getCollectionData(querySnapshot);

      return exceptions;
    });
  }

  /**
   * Get exceptions by user ID
   * @param {string} userId - User ID
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Array>} Array of user exceptions
   */
  async getExceptionsByUserId(userId, organizationId) {
    return safeExecute(async () => {
      const exceptionsRef = collection(db, this.getExceptionsPath(organizationId));
      const exceptionsQuery = query(
        exceptionsRef, 
        where('user.id', '==', userId)
      );
      
      const querySnapshot = await getDocs(exceptionsQuery);
      const exceptions = getCollectionData(querySnapshot);

      return exceptions;
    });
  }
}

export default ExceptionRepository;
